import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import IntegrationsService, {
  type AllodonsAssociation,
  type CampaignAssociation,
  type IntegrationKey,
  type IntegrationState,
  type IntegrationStatus,
} from '../services/integrations'

/**
 * #270 — intégrations actives de l'organisation (`GET /integrations`).
 *
 * Une seule requête partagée par toute l'application (barre latérale, fiche,
 * numéro inconnu, historique donateur, onglet Dons) : `staleTime` 60 s, clé
 * par organisation (le compte système en désigne une sur la page
 * Intégrations). Invalidée après chaque geste de raccordement
 * (`useInvalidateIntegrations`).
 *
 * RÈGLE D'AFFICHAGE : tant que la lecture n'a pas abouti — chargement, échec,
 * réponse hors contrat — une intégration est tenue pour INACTIVE. Rien
 * d'AlloDons n'apparaît puis ne disparaît (pas de clignotement), et un
 * serveur d'avant le lot rend simplement l'application « sans AlloDons ».
 */
export const INTEGRATIONS_QUERY_KEY = ['integrations'] as const
export const INTEGRATIONS_STALE_MS = 60_000
/**
 * Retouche P3 (recette banc) : la barre latérale reste MONTÉE d'une page à
 * l'autre — sans remontage ni focus (headless, iPad posé), `staleTime` seul ne
 * relit jamais. Relecture périodique (un admin qui raccorde sur un autre
 * appareil voit le menu sous une minute) et au retour sur l'onglet. Une requête
 * minuscule, servie par le cache serveur de 30 s.
 */
export const INTEGRATIONS_REFETCH_MS = 60_000

export function useIntegrations(tenantId?: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...INTEGRATIONS_QUERY_KEY, tenantId ?? 'self'],
    queryFn: () => IntegrationsService.list(tenantId),
    staleTime: INTEGRATIONS_STALE_MS,
    refetchInterval: INTEGRATIONS_REFETCH_MS,
    refetchOnWindowFocus: true,
    enabled: options.enabled ?? true,
    // Pas de nouvelle tentative : l'échec vaut « inactif », et la prochaine
    // lecture (60 s, ou une invalidation) réessaiera d'elle-même.
    retry: false,
  })
}

export interface UseIntegrationResult {
  /** `true` SEULEMENT quand le serveur l'a dit — faux pendant le chargement. */
  enabled: boolean
  /** Lecture en cours (première fois) : ne rien afficher de l'intégration. */
  loading: boolean
  status: IntegrationStatus
  integration: IntegrationState | null
}

export function useIntegration(key: IntegrationKey, tenantId?: string): UseIntegrationResult {
  const query = useIntegrations(tenantId)
  const integration = query.data?.find((entry) => entry.key === key) ?? null
  return {
    enabled: integration?.enabled === true,
    loading: query.isPending,
    status: integration?.status ?? 'not_connected',
    integration,
  }
}

export interface UseCampaignAssociationResult {
  /**
   * L'appel relève d'une association AlloDons raccordée : onglet Dons (et,
   * demain, saisie de don #245) à afficher. Faux pendant la lecture.
   */
  enabled: boolean
  loading: boolean
  /** L'association de l'appel (`null` = aucune collecte, ou inconnue). */
  association: AllodonsAssociation | null
  source: CampaignAssociation['source']
}

/**
 * #253 — « de quelle association relève CET appel ? » : celle de la campagne
 * (`Campaign.allodonsConnectionId`), ou — sans campagne (mode manuel) —
 * l'association par défaut. Une campagne « sans collecte » ⇒ rien.
 *
 * Ne part PAS tant qu'AlloDons n'est pas raccordé pour l'organisation (même
 * règle d'affichage que `useIntegration` : rien pendant la lecture). Clé sous
 * `INTEGRATIONS_QUERY_KEY` : un geste de raccordement la relit aussi.
 *
 * Serveur d'avant #253 (route absente, 404) : on retombe sur la règle de
 * #270 — Dons partout dès qu'AlloDons est raccordé — plutôt que de faire
 * disparaître l'onglet le temps d'un déploiement décalé.
 */
export function useCampaignAssociation(
  campaignId: string | null | undefined,
): UseCampaignAssociationResult {
  const allodons = useIntegration('allodons')
  const query = useQuery({
    queryKey: [...INTEGRATIONS_QUERY_KEY, 'allodons-association', campaignId || 'manual'],
    queryFn: () => IntegrationsService.campaignAssociation(campaignId || null),
    staleTime: INTEGRATIONS_STALE_MS,
    enabled: allodons.enabled,
    retry: false,
  })

  if (!allodons.enabled) {
    return { enabled: false, loading: allodons.loading, association: null, source: 'none' }
  }
  if (query.isError) {
    return { enabled: true, loading: false, association: null, source: 'default' }
  }
  const association = query.data?.association ?? null
  return {
    enabled: Boolean(association),
    loading: query.isPending,
    association,
    source: association ? (query.data?.source ?? 'none') : 'none',
  }
}

/** Après un geste de raccordement : relire l'état de TOUTES les organisations en cache. */
export function useInvalidateIntegrations(): () => Promise<void> {
  const queryClient = useQueryClient()
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY }),
    [queryClient],
  )
}
